import { useEffect, useState } from "react";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
} from "react-router-dom";

import "../css/Sidebar.css";
import LogoSenaGestor from "../image/LogoSenaGestor.png";
import DropdownMenu from "./DropdownMenu";

const Sidebar = ({
  sidebarRef,
  isOpen,
  onCloseSidebar,
}) => {
  const [activeDropdown, setActiveDropdown] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  /* =========================================================
     UTILIDADES
  ========================================================= */

  const normalizarTexto = (texto = "") =>
    String(texto)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");

  const leerStorage = (key, valorDefault = {}) => {
    try {
      const item = localStorage.getItem(key);

      return item
        ? JSON.parse(item)
        : valorDefault;
    } catch (error) {
      console.error(
        `Error leyendo ${key} desde localStorage:`,
        error
      );

      return valorDefault;
    }
  };

  /* =========================================================
     INFORMACIÓN DE SESIÓN
  ========================================================= */

  const usuariosStorage = leerStorage("usuarios", {});
  const usuarioStorage = leerStorage("usuario", {});
  const userStorage = leerStorage("user", {});
  const permisosStorage = leerStorage("permisos", {});

  const textoSesion = normalizarTexto(
    JSON.stringify({
      usuariosStorage,
      usuarioStorage,
      userStorage,
      permisosStorage,
    })
  );

  const esSuperAdmin =
    textoSesion.includes("superadmin") ||
    textoSesion.includes("super_admin");

  const modulosPermisos =
    permisosStorage?.modulos ||
    permisosStorage?.permisos ||
    usuariosStorage?.modulos ||
    usuariosStorage?.permisos ||
    usuarioStorage?.modulos ||
    usuarioStorage?.permisos ||
    userStorage?.modulos ||
    userStorage?.permisos ||
    [];

  /* =========================================================
     VALIDAR SI EL USUARIO PUEDE VER UN MÓDULO
  ========================================================= */

  const puedeVerModulo = (nombreModulo) => {
    if (esSuperAdmin) {
      return true;
    }

    const moduloBuscado =
      normalizarTexto(nombreModulo);

    const modulo = Array.isArray(modulosPermisos)
      ? modulosPermisos.find((item) => {
          const nombre =
            item?.nombreModulo ||
            item?.modulo ||
            item?.nombre ||
            item?.name ||
            item?.moduloObjectId?.nombreModulo ||
            item?.moduloObjectId?.nombre ||
            "";

          return (
            normalizarTexto(nombre) ===
            moduloBuscado
          );
        })
      : null;

    return (
      modulo?.puedeLeer === true ||
      modulo?.leer === true ||
      modulo?.read === true
    );
  };

  /*
   * Todo el bloque de cobros y suscripciones
   * utiliza actualmente un mismo módulo de permisos:
   *
   * CobrosSuscripciones
   */
  const puedeVerCobrosSuscripciones =
    puedeVerModulo("CobrosSuscripciones");

  /*
   * Todo el bloque de auditoría utiliza un mismo módulo
   * de permisos:
   *
   * Auditoria
   *
   * El SuperAdmin lo visualiza automáticamente porque
   * puedeVerModulo() ya contempla ese rol.
   */
  const puedeVerAuditoria =
    puedeVerModulo("Auditoria");

  /* =========================================================
     MENÚ INSTITUCIONAL
  ========================================================= */

  const linksInstitucional = [
    puedeVerModulo("Regionales") && {
      to: "/institucional/regional/",
      label: "Regional",
      iconClass: "fas fa-location-dot",
    },

    puedeVerModulo("Centros") && {
      to: "/institucional/centro/",
      label: "Centro",
      iconClass: "fas fa-building",
    },

    puedeVerModulo("ProgramasFormacion") && {
      to: "/institucional/programas/",
      label: "Programa",
      iconClass: "fas fa-book-open",
    },

    puedeVerModulo("ProgramaInstructores") && {
      to: "/programainstructores",
      label: "Mis Programas",
      iconClass: "fa-solid fa-chalkboard-user",
    },

    puedeVerModulo("Grupos") && {
      to: "/institucional/grupos/",
      label: "Grupos",
      iconClass: "fas fa-users",
    },

    puedeVerModulo("Grupo Instructores") && {
      to: "/grupoinstructores",
      label: "Mis Grupos",
      iconClass: "fas fa-user-plus",
    },
  ].filter(Boolean);

  /* =========================================================
     MENÚ ENTE CONFORMADOR
  ========================================================= */

  const linksEnteConformador = [
    puedeVerModulo("EntesConformadores") && {
      to: "/enteconformador/ente/",
      label: "Listar Entes",
      iconClass: "fas fa-shield-alt",
    },

    puedeVerModulo("JefesInmediatos") && {
      to: "/enteconformador/jefes/",
      label: "Listar Jefes",
      iconClass: "fas fa-bell",
    },
  ].filter(Boolean);

  /* =========================================================
     MENÚ MATRÍCULA
  ========================================================= */

  const linksMatricula = [
    puedeVerModulo("Matriculas") && {
      to: "/matricula/listarmatriculas/",
      label: "Listar Matrículas",
      iconClass: "fa-solid fa-list-check",
    },

    puedeVerModulo("TarjetaUID") && {
      to: "/tarjeta/listartarjetaaprendiz/",
      label: "Tarjeta UID",
      iconClass: "fa-solid fa-id-card",
    },

    puedeVerModulo("EtapaProductiva") && {
      to: "/etapaproductiva/listareproductiva/",
      label: "Etapa Productiva",
      iconClass: "fa-solid fa-briefcase",
    },
  ].filter(Boolean);

  /* =========================================================
     MENÚ AUDITORÍA
  ========================================================= */

  const linksAuditoria = [
    puedeVerAuditoria && {
      to: "/auditoria/documento",
      label: "Panel Documental",
      iconClass: "fa-solid fa-file-circle-check",
    },

    puedeVerAuditoria && {
      to: "/auditoria/tipo-documento",
      label: "Tipos de Documento",
      iconClass: "fa-solid fa-folder-tree",
    },

    puedeVerAuditoria && {
      to: "/auditoria/criterio",
      label: "Criterios",
      iconClass: "fa-solid fa-list-check",
    },

    puedeVerAuditoria && {
      to: "/auditoria/asignacion",
      label: "Asignaciones",
      iconClass: "fa-solid fa-user-check",
    },

    puedeVerAuditoria && {
      to: "/auditoria/revision",
      label: "Historial",
      iconClass: "fa-solid fa-clock-rotate-left",
    },
  ].filter(Boolean);

  /* =========================================================
     MENÚ REPORTES
  ========================================================= */

  const linksReportes = [
    puedeVerModulo("Reportes") && {
      to: "/reportes",
      label: "Diarios",
      iconClass: "fas fa-calendar-day",
    },
  ].filter(Boolean);

  /* =========================================================
     MENÚ CONFIGURACIONES

     Aquí queda TODO el nuevo módulo:
     - Roles y Permisos
     - Notificaciones
     - Configuración de Cobros
     - Planes
     - Suscripciones
     - Pagos
     - Historial
  ========================================================= */

  const linksConfiguraciones = [
    puedeVerModulo("RolesyPermisos") && {
      to: "/configuraciones/asignarmodulos",
      label: "Roles y Permisos",
      iconClass: "fas fa-user-shield",
    },

    puedeVerModulo("Notificaciones") && {
      to: "/configuraciones/notificaciones",
      label: "Notificaciones",
      iconClass: "fas fa-bell",
    },

    puedeVerCobrosSuscripciones && {
      to: "/cobros/planes",
      label: "Planes",
      iconClass: "fa-solid fa-tags",
    },

    puedeVerCobrosSuscripciones && {
      to: "/cobros/configuracion",
      label: "Configuración de Cobros",
      iconClass: "fa-solid fa-sliders",
    },

    puedeVerCobrosSuscripciones && {
      to: "/cobros/suscripciones",
      label: "Suscripciones",
      iconClass: "fa-solid fa-id-card-clip",
    },

    puedeVerCobrosSuscripciones && {
      to: "/cobros/pagos",
      label: "Pagos",
      iconClass: "fa-solid fa-money-bill-wave",
    },

    puedeVerCobrosSuscripciones && {
      to: "/cobros/historial",
      label: "Historial",
      iconClass: "fa-solid fa-clock-rotate-left",
    },
  ].filter(Boolean);

  /* =========================================================
     ABRIR / CERRAR DROPDOWN
  ========================================================= */

  const toggleDropdown = (dropdownId) => {
    setActiveDropdown((actual) =>
      actual === dropdownId
        ? null
        : dropdownId
    );
  };

  /* =========================================================
     CERRAR MENÚ AL HACER CLIC EN UN LINK EN MÓVIL
  ========================================================= */

  const handleLinkClick = () => {
    if (window.innerWidth <= 767.98) {
      onCloseSidebar?.();
    }
  };

  /* =========================================================
     CERRAR SIDEBAR AL HACER CLIC FUERA EN MÓVIL
  ========================================================= */

  useEffect(() => {
    let timeout;

    const handleOutsideClick = (e) => {
      if (
        isOpen &&
        sidebarRef?.current &&
        !sidebarRef.current.contains(e.target) &&
        window.innerWidth <= 767.98
      ) {
        onCloseSidebar?.();
      }
    };

    if (isOpen) {
      timeout = setTimeout(() => {
        document.addEventListener(
          "mousedown",
          handleOutsideClick
        );
      }, 100);
    }

    return () => {
      clearTimeout(timeout);

      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, [
    isOpen,
    onCloseSidebar,
    sidebarRef,
  ]);

  /* =========================================================
     CERRAR SIDEBAR AL CAMBIAR DE RUTA EN MÓVIL
  ========================================================= */

  useEffect(() => {
    if (
      window.innerWidth <= 767.98 &&
      isOpen
    ) {
      onCloseSidebar?.();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* =========================================================
     MANTENER ABIERTO EL DROPDOWN SEGÚN LA RUTA
  ========================================================= */

  useEffect(() => {
    const pathname = location.pathname;

    if (
      pathname.startsWith("/institucional") ||
      pathname.startsWith("/programainstructores") ||
      pathname.startsWith("/grupoinstructores")
    ) {
      setActiveDropdown(
        "institucionalDropdown"
      );

      return;
    }

    if (
      pathname.startsWith(
        "/enteconformador"
      )
    ) {
      setActiveDropdown(
        "enteConformadorDropdown"
      );

      return;
    }

    if (
      pathname.startsWith("/matricula") ||
      pathname.startsWith("/tarjeta") ||
      pathname.startsWith(
        "/etapaproductiva"
      )
    ) {
      setActiveDropdown(
        "matriculaDropdown"
      );

      return;
    }

    if (
      pathname.startsWith("/auditoria")
    ) {
      setActiveDropdown(
        "auditoriaDropdown"
      );

      return;
    }

    if (
      pathname.startsWith("/reportes")
    ) {
      setActiveDropdown(
        "reportesDropdown"
      );

      return;
    }

    if (
      pathname.startsWith(
        "/configuraciones"
      ) ||
      pathname.startsWith("/cobros")
    ) {
      setActiveDropdown(
        "configuracionDropdown"
      );
    }
  }, [location.pathname]);

  /* =========================================================
     CERRAR SESIÓN
  ========================================================= */

  const handleLogout = () => {
    localStorage.removeItem("usuarios");
    localStorage.removeItem("usuario");
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("permisos");

    navigate("/", {
      replace: true,
    });
  };

  /* =========================================================
     VISTA
  ========================================================= */

  return (
    <nav
      id="sidebarMenu"
      ref={sidebarRef}
      className={`sidebar-custom d-md-block ${
        isOpen
          ? "show-mobile"
          : "d-none d-md-block"
      }`}
    >
      <div className="position-sticky pt-4 h-100 d-flex flex-column">

        {/* =====================================================
            LOGO
        ===================================================== */}

        <div className="sidebar-header text-center mb-4">
          <img
            src={LogoSenaGestor}
            alt="Logo"
            style={{
              height: "200px",
            }}
          />
        </div>

        {/* =====================================================
            CERRAR MENÚ EN MÓVIL
        ===================================================== */}

        <div className="text-center d-md-none mb-3">
          <button
            className="btn btn-outline-light"
            type="button"
            onClick={onCloseSidebar}
          >
            Cerrar menú
          </button>
        </div>

        {/* =====================================================
            MENÚ PRINCIPAL
        ===================================================== */}

        <ul className="nav flex-column mb-auto">

          {/* Inicio */}
          <li className="nav-item">
            <Link
              to="/inicio"
              onClick={handleLinkClick}
              className="nav-link text-white d-flex align-items-center"
            >
              <i className="fas fa-home me-2"></i>

              <span>
                Inicio
              </span>
            </Link>
          </li>

          {/* Mi Perfil */}
          <li className="nav-item">
            <Link
              to="/perfil"
              onClick={handleLinkClick}
              className="nav-link text-white d-flex align-items-center"
            >
              <i className="fas fa-user me-2"></i>

              <span>
                Mi Perfil
              </span>
            </Link>
          </li>

          {/* Mi Suscripción */}
          <li className="nav-item">
            <NavLink
              to="/mi-suscripcion"
              onClick={handleLinkClick}
              className={({ isActive }) =>
                `nav-link d-flex align-items-center gap-2 px-3 py-2 rounded-3 text-white ${
                  isActive
                    ? "active fw-semibold"
                    : ""
                }`
              }
              title="Mi Suscripción"
            >
              <i className="fa-solid fa-credit-card"></i>

              <span>
                Mi Suscripción
              </span>
            </NavLink>
          </li>

          {/* Instructor */}
          {puedeVerModulo("Instructores") && (
            <NavLink
              to="/instructor/"
              onClick={handleLinkClick}
              className={({ isActive }) =>
                `nav-link d-flex align-items-center gap-2 px-3 py-2 rounded-3 text-white ${
                  isActive
                    ? "active fw-semibold"
                    : ""
                }`
              }
              title="Instructor"
            >
              <i className="fa-solid fa-chalkboard-user"></i>

              <span>
                Instructor
              </span>
            </NavLink>
          )}

          {/* Aprendices */}
          {puedeVerModulo("Aprendices") && (
            <NavLink
              to="/aprendiz/"
              onClick={handleLinkClick}
              className={({ isActive }) =>
                `nav-link d-flex align-items-center gap-2 px-3 py-2 rounded-3 text-white ${
                  isActive
                    ? "active fw-semibold"
                    : ""
                }`
              }
              title="Aprendices"
            >
              <i className="fa-solid fa-graduation-cap"></i>

              <span>
                Aprendices
              </span>
            </NavLink>
          )}

          {/* ===================================================
              INSTITUCIONAL
          =================================================== */}

          {linksInstitucional.length > 0 && (
            <DropdownMenu
              id="institucionalDropdown"
              iconClass="fa-solid fa-building-columns"
              label="Institucional"
              submenuId="submenuInstitucional"
              isOpen={
                activeDropdown ===
                "institucionalDropdown"
              }
              toggleDropdown={() =>
                toggleDropdown(
                  "institucionalDropdown"
                )
              }
              links={linksInstitucional}
              onLinkClick={handleLinkClick}
            />
          )}

          {/* ===================================================
              ENTE CONFORMADOR
          =================================================== */}

          {linksEnteConformador.length > 0 && (
            <DropdownMenu
              id="enteConformadorDropdown"
              iconClass="fa-solid fa-school"
              label="Ente Conformador"
              submenuId="submenuEnteConformador"
              isOpen={
                activeDropdown ===
                "enteConformadorDropdown"
              }
              toggleDropdown={() =>
                toggleDropdown(
                  "enteConformadorDropdown"
                )
              }
              links={linksEnteConformador}
              onLinkClick={handleLinkClick}
            />
          )}

          {/* ===================================================
              MATRÍCULA
          =================================================== */}

          {linksMatricula.length > 0 && (
            <DropdownMenu
              id="matriculaDropdown"
              iconClass="fa-solid fa-user-graduate"
              label="Matrícula"
              submenuId="submenuMatricula"
              isOpen={
                activeDropdown ===
                "matriculaDropdown"
              }
              toggleDropdown={() =>
                toggleDropdown(
                  "matriculaDropdown"
                )
              }
              links={linksMatricula}
              onLinkClick={handleLinkClick}
            />
          )}

          {/* ===================================================
              AUDITORÍA
          =================================================== */}

          {linksAuditoria.length > 0 && (
            <DropdownMenu
              id="auditoriaDropdown"
              iconClass="fa-solid fa-magnifying-glass-chart"
              label="Auditoría"
              submenuId="submenuAuditoria"
              isOpen={
                activeDropdown ===
                "auditoriaDropdown"
              }
              toggleDropdown={() =>
                toggleDropdown(
                  "auditoriaDropdown"
                )
              }
              links={linksAuditoria}
              onLinkClick={handleLinkClick}
            />
          )}

          {/* ===================================================
              REPORTES
          =================================================== */}

          {linksReportes.length > 0 && (
            <DropdownMenu
              id="reportesDropdown"
              iconClass="fas fa-chart-line"
              label="Reportes"
              submenuId="submenuReportes"
              isOpen={
                activeDropdown ===
                "reportesDropdown"
              }
              toggleDropdown={() =>
                toggleDropdown(
                  "reportesDropdown"
                )
              }
              links={linksReportes}
              onLinkClick={handleLinkClick}
            />
          )}

          {/* ===================================================
              CONFIGURACIÓN
          =================================================== */}

          {linksConfiguraciones.length > 0 && (
            <DropdownMenu
              id="configuracionDropdown"
              iconClass="fas fa-cog"
              label="Configuración"
              submenuId="submenuConfiguraciones"
              isOpen={
                activeDropdown ===
                "configuracionDropdown"
              }
              toggleDropdown={() =>
                toggleDropdown(
                  "configuracionDropdown"
                )
              }
              links={linksConfiguraciones}
              onLinkClick={handleLinkClick}
            />
          )}

          {/* ===================================================
              AYUDA
          =================================================== */}

          <li className="nav-item">
            <Link
              className="nav-link text-white d-flex align-items-center"
              to="/ayuda"
              onClick={handleLinkClick}
            >
              <i className="fa-solid fa-handshake-angle me-2"></i>

              <span>
                Ayuda
              </span>
            </Link>
          </li>

        </ul>

        {/* =====================================================
            CERRAR SESIÓN
        ===================================================== */}

        <div className="mt-auto d-flex justify-content-center mb-3">
          <button
            className="btn btn-outline-light"
            type="button"
            onClick={handleLogout}
          >
            Cerrar sesión
          </button>
        </div>

      </div>
    </nav>
  );
};

export default Sidebar;